import type { HabitFrequency } from '@/types/goal';

// Day indices: 0=Sun, 1=Mon, 2=Tue, 3=Wed, 4=Thu, 5=Fri, 6=Sat
const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const DAY_INITIALS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'] as const;

export { DAY_LABELS, DAY_INITIALS };

export interface SchedulePreset {
  frequency: HabitFrequency;
  scheduleDays: number | null;
  label: string;
  shortLabel: string;
}

export const SCHEDULE_PRESETS: SchedulePreset[] = [
  { frequency: 'daily', scheduleDays: 127, label: 'Every day', shortLabel: 'Daily' },
  { frequency: 'weekdays', scheduleDays: 62, label: 'Weekdays (Mon–Fri)', shortLabel: 'Weekdays' },
  { frequency: 'specific_days', scheduleDays: 42, label: 'Mon / Wed / Fri', shortLabel: 'MWF' },
  { frequency: 'specific_days', scheduleDays: 20, label: 'Tue / Thu', shortLabel: 'Tue/Thu' },
  { frequency: 'weekly', scheduleDays: null, label: 'Once per week', shortLabel: 'Weekly' },
  { frequency: 'every_other_day', scheduleDays: null, label: 'Every other day', shortLabel: 'Alt. days' },
];

export function bitmaskFromDayIndices(indices: number[]): number {
  let mask = 0;
  for (const i of indices) {
    mask |= (1 << i);
  }
  return mask;
}

export function dayIndicesFromBitmask(mask: number): number[] {
  const indices: number[] = [];
  for (let i = 0; i < 7; i++) {
    if (mask & (1 << i)) {
      indices.push(i);
    }
  }
  return indices;
}

/**
 * Returns true if the habit is due on the given date.
 * Weekly habits return true every day (display layer handles "completed this week" separately).
 */
export function isHabitDueToday(
  frequency: HabitFrequency,
  scheduleDays: number | null,
  createdAt: string,
  date: Date = new Date()
): boolean {
  const dayOfWeek = date.getDay(); // 0=Sun, 6=Sat

  switch (frequency) {
    case 'daily':
      return true;

    case 'weekdays':
      return dayOfWeek >= 1 && dayOfWeek <= 5;

    case 'specific_days':
      if (scheduleDays == null) return true;
      return (scheduleDays & (1 << dayOfWeek)) !== 0;

    case 'weekly':
      // Weekly habits are "due" every day — the display layer checks weekly completion
      return true;

    case 'every_other_day': {
      const created = new Date(createdAt);
      created.setHours(0, 0, 0, 0);
      const target = new Date(date);
      target.setHours(0, 0, 0, 0);
      const diffDays = Math.round((target.getTime() - created.getTime()) / (1000 * 60 * 60 * 24));
      return diffDays % 2 === 0;
    }

    default:
      return true;
  }
}

/**
 * Walk backwards from `date` to find the previous due date (not including `date` itself).
 * Returns null if no previous due date found within 90 days.
 */
export function getPreviousDueDate(
  frequency: HabitFrequency,
  scheduleDays: number | null,
  createdAt: string,
  date: Date = new Date()
): Date | null {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);

  for (let i = 1; i <= 90; i++) {
    d.setDate(d.getDate() - 1);
    if (isHabitDueToday(frequency, scheduleDays, createdAt, d)) {
      return new Date(d);
    }
  }
  return null;
}

/**
 * Count how many scheduled days fall within the week containing `date`.
 * Week starts on Monday.
 */
export function getScheduledDaysInWeek(
  frequency: HabitFrequency,
  scheduleDays: number | null,
  createdAt: string,
  date: Date = new Date()
): number {
  // Find start of week (Monday)
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day; // Monday = 1
  d.setDate(d.getDate() + diff);

  let count = 0;
  for (let i = 0; i < 7; i++) {
    const weekDay = new Date(d);
    weekDay.setDate(weekDay.getDate() + i);
    if (isHabitDueToday(frequency, scheduleDays, createdAt, weekDay)) {
      count++;
    }
  }
  return count;
}

/**
 * Human-readable label for a schedule.
 */
export function getScheduleLabel(frequency: HabitFrequency, scheduleDays: number | null): string {
  // Check presets first
  const preset = SCHEDULE_PRESETS.find(
    p => p.frequency === frequency && p.scheduleDays === scheduleDays
  );
  if (preset) return preset.label;

  // Custom specific_days
  if (frequency === 'specific_days' && scheduleDays != null) {
    const indices = dayIndicesFromBitmask(scheduleDays);
    if (indices.length === 0) return 'No days selected';
    return indices.map(i => DAY_LABELS[i]).join(', ');
  }

  // Fallback to frequency name
  switch (frequency) {
    case 'daily': return 'Every day';
    case 'weekdays': return 'Weekdays';
    case 'weekly': return 'Once per week';
    case 'every_other_day': return 'Every other day';
    default: return frequency;
  }
}
